import { formatCompactINR, formatNum, formatINR } from "../../lib/format";
import { Skeleton } from "../ui/skeleton";
import { Wallet, Users, Target, TrendingUp, ArrowUpRight, ArrowDownRight } from "lucide-react";

function Card({ children, testid, glow }) {
  return (
    <div data-testid={testid}
      className={`rounded-xl bg-[#1E293B] border border-white/10 p-5 transition-transform hover:-translate-y-0.5 hover:bg-[#22304a] ${glow || ""}`}>
      {children}
    </div>
  );
}

function Label({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 text-slate-400">
      <Icon className="w-4 h-4" />
      <span className="text-xs tracking-[0.1em] uppercase">{children}</span>
    </div>
  );
}

export default function KpiRow({ summary, loading }) {
  if (loading || !summary) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-36 rounded-xl bg-[#1E293B]" />
        ))}
      </div>
    );
  }

  const pacing = summary.pacing_status;
  const pacingColor = pacing === "over" ? "#EF4444" : pacing === "under" ? "#F59E0B" : "#10B981";
  const pacingLabel = pacing === "over" ? "Over-spending" : pacing === "under" ? "Under-spending" : "On Track";

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6 animate-fade-up">
      <Card testid="kpi-budget" glow={pacing === "over" ? "glow-red" : ""}>
        <Label icon={Wallet}>Budget Utilization (MTD)</Label>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="font-display text-3xl font-bold tracking-tighter tabular">
            {formatCompactINR(summary.month_spent)}
          </span>
          <span className="text-sm text-slate-500">/ {formatCompactINR(summary.total_budget)}</span>
        </div>
        <div className="mt-3">
          <div className="h-2 rounded-full bg-[#0F172A] overflow-hidden">
            <div className="h-full rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${Math.min(summary.spend_pct, 100)}%`, backgroundColor: pacingColor }} />
          </div>
          <div className="flex justify-between mt-2 text-xs">
            <span className="text-slate-400">{summary.spend_pct}% spent</span>
            <span style={{ color: pacingColor }} className="font-medium">{pacingLabel}</span>
          </div>
        </div>
      </Card>

      <Card testid="kpi-leads">
        <Label icon={Users}>Leads (selected range)</Label>
        <div className="mt-3 font-display text-3xl font-bold tracking-tighter tabular">
          {formatNum(summary.total_leads)}
        </div>
        <div className="mt-4 flex items-center gap-1.5 text-xs text-[#10B981]">
          <ArrowUpRight className="w-3.5 h-3.5" />
          <span>{formatNum(summary.total_clicks)} clicks · {summary.avg_ctr}% CTR</span>
        </div>
      </Card>

      <Card testid="kpi-cpl">
        <Label icon={Target}>Avg Cost / Lead</Label>
        <div className="mt-3 font-display text-3xl font-bold tracking-tighter tabular">
          {formatINR(summary.avg_cpl)}
        </div>
        <div className="mt-4 text-xs text-slate-400">
          Projected EOM: <span className="text-slate-200">{formatCompactINR(summary.projected_spend)}</span>
        </div>
      </Card>

      <Card testid="kpi-roas">
        <Label icon={TrendingUp}>Blended ROAS</Label>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="font-display text-3xl font-bold tracking-tighter tabular text-[#10B981]">
            {summary.blended_roas}x
          </span>
        </div>
        <div className="mt-4 flex items-center gap-1.5 text-xs text-slate-400">
          <ArrowUpRight className="w-3.5 h-3.5 text-[#10B981]" />
          Est. revenue {formatCompactINR(summary.est_revenue)}
        </div>
      </Card>
    </div>
  );
}
